import React, {ReactElement, ReactNode} from 'react';
import {cx} from '@emotion/css';

import {ArrowIcon, ClientOutlined as ClientOutlinedIcon} from '@fo/root/designSystem/atoms/Icons';
import {IconSize} from '@fo/root/designSystem/atoms/Icons/IconWrapper';
import Button, {ButtonVariant} from '@fo/designSystem/Buttons/Button/Button';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';
import {useIdentityContext} from '@fo/shared-providers/IdentityContext';
import CurrentUser from '@fo/components/Identity/CurrentUser/CurrentUser';
import Dropdown from '@fo/designSystem/Dropdown/Dropdown';
import CurrentUserMenu from '@fo/components/Identity/CurrentUser/CurrentUserMenu';
import useIdentityModal from '@fo/components/Identity/useIdentityModal';
import {INavigation} from '@fo/designSystem/Navigation/types';
import NavigationSection from '@fo/designSystem/Navigation/NavigationSection/NavigationSection';
import FundingOptionsLogo from '@fo/designSystem/FundingOptionsLogo/FundingOptionsLogo';
import {segmentEvents, track} from '@fo/shared-analytics/tracking/segment';
import {ESource, useRedirectToTide} from '@fo/shared-hooks/useRedirectToTide';

import styles from './NavigationDesktop.styles';

function NavigationDesktop({
  ctaButton,
  loginMenuText,
  alternativeJourneyLink,
  sections,
  hideTideBranding,
  isSkipQuoteFormAndRedirectToTide,
}: INavigation): ReactElement {
  const {isLoggedIn} = useIdentityContext();
  const {showLoginModal} = useIdentityModal();
  const {redirectToTide} = useRedirectToTide();

  const ctaButtonHref =
    ctaButton?.url.includes(getPageRoute('quoteForm.start').replace(/\//g, '')) && isSkipQuoteFormAndRedirectToTide
      ? // set as undefined so the Button's onClick will be used instead
        undefined
      : // or, if it's not pointing to /get-started, use Contentful's button url
        ctaButton?.url;

  return (
    <nav className={cx(styles.componentWrapper)} data-testid="ui.navigation.desktop">
      {/*
       * Top bar: Alternative link - Menu
       */}
      <section className={cx(styles.topBar)}>
        <div className={cx(styles.topBarContents)}>
          <DynamicLink
            className={cx(styles.link, 'unstyledLink')}
            href={alternativeJourneyLink.url}
            onClick={() => {
              track(segmentEvents.navigation.accountDetailsButton);
            }}
          >
            <span>{alternativeJourneyLink.label}</span>
            <ArrowIcon size={IconSize.SMALL} />
          </DynamicLink>
          <div>
            {isLoggedIn ? (
              <Dropdown hideCaretMobile buttonContent={<CurrentUser />} className={cx(styles.user)}>
                <CurrentUserMenu />
              </Dropdown>
            ) : (
              <Button
                onClick={showLoginModal}
                variant={ButtonVariant.UNSTYLED}
                ariaLabel={loginMenuText}
                testId="button.navigation.login"
                className={cx(styles.login)}
              >
                <span className={cx(styles.loginText)}>{loginMenuText}</span>
                <ClientOutlinedIcon size={IconSize.SMALL} />
              </Button>
            )}
          </div>
        </div>
      </section>
      {/*
       * Middle bar: Logo - sections labels - CTA
       */}
      <section className={cx(styles.mainBar)}>
        <div className={cx(styles.mainBarContents)}>
          <DynamicLink href={getPageRoute('home')}>
            <FundingOptionsLogo className={cx(styles.logo)} hideTideBranding={hideTideBranding} />
          </DynamicLink>
          <div className={cx(styles.sectionsLabelsContainer)}>
            {sections.map((section): ReactNode => {
              // Used to hide the business services section on tsb partner page
              if (hideTideBranding && section.label.includes('Tide')) {
                return null;
              }
              return (
                <div
                  key={section.label}
                  className={cx(styles.sectionLabelTrigger)}
                  data-testid="ui.navigation.section.label"
                >
                  <span>{section.label}</span>
                  <ArrowIcon size={IconSize.SMALL} className={cx(styles.sectionLabelTriggerCaret)} />
                  {/*
                   * Sections card and links
                   */}
                  <section className={cx(styles.sectionsContentsWrapper)} id="navigation__sectionsContentsWrapper">
                    <div className={cx(styles.sectionsContents)}>
                      <NavigationSection
                        key={`section-${section.label}`}
                        card={section.card}
                        col1={section.col1}
                        col2={section.col2}
                        col3={section.col3}
                      />
                    </div>
                  </section>
                </div>
              );
            })}
          </div>
          {ctaButton ? (
            <div>
              <Button
                key={ctaButton.label}
                ariaLabel={ctaButton.label}
                href={ctaButtonHref}
                testId="button.navigation.cta"
                type="button"
                variant={ButtonVariant.CALL_TO_ACTION}
                onClick={() => {
                  track(segmentEvents.navigation.mainCTAButton);
                  if (isSkipQuoteFormAndRedirectToTide && !ctaButtonHref) {
                    redirectToTide({source: ESource.FROM_CTA_TO_GET_STARTED});
                  }
                }}
              >
                <span>{ctaButton.label}</span>
              </Button>
            </div>
          ) : null}
        </div>
      </section>
    </nav>
  );
}

export default NavigationDesktop;
